import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Loader2, ShieldOff } from "lucide-react";

import Sidebar, { allNavItems } from "./Sidebar";
import TopBar from "./TopBar";
import WelcomeHero from "./WelcomeHero";
import Starfield from "./Starfield";
import FloatingAIBot from "./FloatingAIBot";
import NotificationCenter from "./NotificationCenter";
import { CustomerDataProvider } from "./CustomerDataContext";
import { growthosFetch } from "../config/api";

// import HeroOverviewSection from "./OverviewSection";
import DashboardPreviewSection from "./DashboardPreviewSection";
// BusinessWorkspaceSection moved to company_home.js — no longer routed here.
import AICommandCenter from "./AICommandCenter";
import CampaignBuilderSection from "./Lead_Management";
import CampaignAutomationSection from "./Campaign_Automation";
import SalesPipelineSection from "./SalesPipelineSection";
import Customer360Section from "./Customer360Section";
import MediaSection from "./Mediasection";
import CompanyAccounting from "./Company_Accounting";
import CompanyApproval from "./Companyapprovals";
import CommunicationHub from "./Communication_Hub";
import LeadGenerationSection from "./LeadGenerationSection";
import AIAssistantSection from "./AIAssistantSection";
import PlatformAnalyticsSection from "./PlatformAnalyticsSection";
import AnalyticsBI from "./Analyticsbi";
import FinalCtaSection from "./FinalCtaSection";
import FooterSection from "./FooterSection";

/* ==========================================================
   This dashboard is mounted at "/app/*" (see App.jsx — the
   pricing page lives at "/" and hands off to "/app/overview").
   Each sidebar/search nav id (e.g. "sec-campaign-builder")
   maps 1:1 to its own route under that base (e.g.
   "/app/campaign-builder"). Keeping the "sec-" ids as the
   single source of truth means Sidebar.jsx and Topbar.jsx
   don't need any changes at all — they still just call
   goToSection(id) exactly as before.

   MODULE ACCESS (Settings > Access Control, layer 2): which of
   these routes a teammate may open is decided per product by the
   owner and fetched below from GET /my-module-access. The backend
   returns the FULL module list for the owner's own session, so
   there's no owner special-case in here — this file just renders
   whatever ids come back. Ids match the route segments exactly
   (nav id minus the "sec-" prefix), which is what lets one list
   drive the sidebar, the <Routes> and the URL guard together.
========================================================== */
const DASHBOARD_BASE = "/app";
const idToPath = (id) => `${DASHBOARD_BASE}/${id.replace(/^sec-/, "")}`;
const pathToModuleId = (pathname) =>
    pathname.replace(`${DASHBOARD_BASE}/`, "").split("/")[0] || "";

// Route segment → component. Only entries whose id the backend
// returns get mounted, so a blocked module has no route at all —
// typing its URL by hand can't reach it either (see the guard below).
const MODULE_ELEMENTS = {
    "dashboard-preview": <DashboardPreviewSection />,
    "ai-command-center": <AICommandCenter />,
    "campaign-builder": <CampaignBuilderSection />,
    "campaign-automation": <CampaignAutomationSection />,
    "sales-pipeline": <SalesPipelineSection />,
    "customer-360": <Customer360Section />,
    media: <MediaSection />,
    company_accounting: <CompanyAccounting />,
    approvals: <CompanyApproval />,
    "communication-hub": <CommunicationHub />,
    "lead-generation": <LeadGenerationSection />,
    "ai-assistant": <AIAssistantSection />,
    analytics: <PlatformAnalyticsSection />,
    "analytics-bi": <AnalyticsBI />,
};

// Chrome, not modules — never gated, so they stay out of the
// access catalog and are always mounted.
const UNGATED_ELEMENTS = {
    cta: <FinalCtaSection />,
    footer: <FooterSection />,
};

const readActiveProductId = (search) =>
    new URLSearchParams(search).get("product") ||
    localStorage.getItem("growthos_active_product_id") ||
    "";

const Growthosdashboard = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    // null = still loading; [] = genuinely no access to anything here.
    const [allowedModuleIds, setAllowedModuleIds] = useState(null);

    // Active sidebar highlight now comes from the current URL, so it
    // stays correct on refresh, back/forward, or a direct link too.
    const activeItem =
        allNavItems.find((item) => idToPath(item.id) === location.pathname)?.id ??
        "sec-overview";

    // Same defensive persistence PricingPage.jsx does — covers arriving
    // here with ?product=<id> from the pricing hand-off, so Lead
    // Generation opens already scoped to that product.
    useEffect(() => {
        const productId = new URLSearchParams(location.search).get("product");
        if (productId) {
            localStorage.setItem("growthos_active_product_id", productId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Which modules this session may see, for THIS product. Refetched
    // when the product changes, since access is per (member, product).
    const activeProductId = readActiveProductId(location.search);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!activeProductId) {
                // No product in context at all — nothing to scope access
                // to, so fall back to the full list rather than locking
                // the whole dashboard on what's really a routing gap.
                setAllowedModuleIds(Object.keys(MODULE_ELEMENTS));
                return;
            }
            setAllowedModuleIds(null);
            const { res, data } = await growthosFetch(
                `/my-module-access?product_id=${activeProductId}`,
                { method: "GET" }
            );
            if (cancelled) return;
            if (res?.status === 401) {
                navigate("/login");
                return;
            }
            if (res?.ok && data?.success) {
                setAllowedModuleIds(data.module_ids || []);
            } else {
                // Don't hard-lock the UI on a transient 500 — a failed
                // check falls back to the full list, and the real
                // per-endpoint auth still applies on every data call.
                setAllowedModuleIds(Object.keys(MODULE_ELEMENTS));
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeProductId]);

    const landingModuleId = allowedModuleIds?.[0] || "";
    const currentModuleId = pathToModuleId(location.pathname);

    // URL guard — someone typing /app/company_accounting directly (or a
    // stale bookmark from before their access changed) gets bounced to
    // their first allowed module instead of a blank screen.
    useEffect(() => {
        if (!allowedModuleIds || allowedModuleIds.length === 0) return;
        const isGated = Object.prototype.hasOwnProperty.call(MODULE_ELEMENTS, currentModuleId);
        if (isGated && !allowedModuleIds.includes(currentModuleId)) {
            navigate(idToPath(landingModuleId), { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowedModuleIds, currentModuleId]);

    // Same function signature Sidebar/TopBar already call —
    // it now routes to that module's own page instead of scrolling.
    const goToSection = (id) => {
        navigate(idToPath(id));
    };

    // WelcomeHero ("Welcome back, Aman...") is overview-only content —
    // every other module gets the full remaining height to itself.
    const isOverview = location.pathname === DASHBOARD_BASE || location.pathname === `${DASHBOARD_BASE}/overview`;

    return (
        // CustomerDataProvider wraps the routed area so Campaign Builder
        // (which fetches/writes leads) and Customer 360 (which reads and
        // displays a selected lead) share the exact same live data —
        // no prop drilling, no duplicate fetches.
        <CustomerDataProvider>
            <div className="min-h-screen w-full bg-black text-gray-300 flex font-sans">
                <Starfield />

                {/* ================= SIDEBAR ================= */}
                {/* allowedModuleIds is passed straight through; Sidebar.jsx
                    filters allNavItems with it (see the 3-line change noted
                    in the handover). null while loading = show everything,
                    so the sidebar doesn't flicker empty on first paint. */}
                <Sidebar
                    sidebarOpen={sidebarOpen}
                    activeItem={activeItem}
                    goToSection={goToSection}
                    allowedModuleIds={allowedModuleIds}
                />

                {/* ================= MAIN ================= */}
                <main className="flex-1 min-w-0 px-8 py-6 h-screen flex flex-col overflow-hidden">
                    {/* Breadcrumb strip — its own row, separated from TopBar by a
                        hairline, so it reads as a deliberate secondary bar rather
                        than trying (and failing) to sit flush with TopBar's own
                        internal padding, which isn't ours to adjust here. */}
                    <div className="-mx-8 mb-4 shrink-0 border-b border-white/[0.06] px-8 pb-3">
                        <button
                            type="button"
                            onClick={() => navigate("/company_home")}
                            className="group inline-flex items-center gap-1.5 text-sm font-medium text-zinc-500 transition duration-200 hover:text-orange-300"
                        >
                            <ArrowLeft
                                size={15}
                                className="transition-transform duration-200 group-hover:-translate-x-0.5"
                            />
                            Back to Products
                        </button>
                    </div>

                    {/* Notification bell — floating for now since TopBar.jsx's own
                        markup isn't in this file. Wire <NotificationCenter mode="company" />
                        directly into TopBar next to its existing bell icon once
                        that file's shared; until then this stays functional here,
                        just visually separate from TopBar's icons. */}
                    <div className="fixed top-6 right-28 z-40">
                        <NotificationCenter mode="company" />
                    </div>

                    {/* Top bar: sidebar toggle, search, notifications */}
                    <TopBar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} goToSection={goToSection} />

                    {/* Hero / welcome — overview page only. Other modules use the
                        full remaining height for a "fit screen" view instead. */}
                    {isOverview && <WelcomeHero />}

                    {/* Routed module — fills remaining viewport height ("fit screen"),
                        only scrolling internally if a module's own content is taller. */}
                    <div
                        className={`flex-1 min-h-0 -mx-8 px-8 overflow-hidden relative z-10 ${
                            isOverview ? "border-t border-orange-600/20" : ""
                        }`}
                    >
                        {allowedModuleIds === null ? (
                            <div className="flex h-full items-center justify-center gap-2 text-sm text-zinc-500">
                                <Loader2 size={15} className="animate-spin" /> Loading your workspace…
                            </div>
                        ) : allowedModuleIds.length === 0 ? (
                            <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                                <ShieldOff size={26} className="text-zinc-600" />
                                <p className="text-sm font-semibold text-zinc-300">
                                    You don't have access to any modules in this product.
                                </p>
                                <p className="max-w-sm text-xs text-zinc-500">
                                    Ask the account owner to grant access from Settings → Access Control.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => navigate("/company_home")}
                                    className="mt-1 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-500"
                                >
                                    Back to Products
                                </button>
                            </div>
                        ) : (
                            <Routes>
                                <Route path="/" element={<Navigate to={landingModuleId} replace />} />
                                {/* <Route path="overview" element={<HeroOverviewSection />} /> */}
                                {allowedModuleIds
                                    .filter((id) => MODULE_ELEMENTS[id])
                                    .map((id) => (
                                        <Route key={id} path={id} element={MODULE_ELEMENTS[id]} />
                                    ))}
                                {Object.entries(UNGATED_ELEMENTS).map(([id, element]) => (
                                    <Route key={id} path={id} element={element} />
                                ))}
                                <Route path="*" element={<Navigate to={landingModuleId} replace />} />
                            </Routes>
                        )}
                    </div>
                </main>

                {/* ================= FLOATING AI BOT ================= */}
                <FloatingAIBot />
            </div>
        </CustomerDataProvider>
    );
};

export default Growthosdashboard;